/**
 * TaskExecutionCockpit — Painel operacional de execução (hero da bancada).
 */
import React from 'react';

export interface CockpitMetric {
  label: string;
  value: string;
  tone?: 'default' | 'ok' | 'warning' | 'danger' | 'info';
}

export type CockpitStatusVariant = 'paused' | 'in_progress' | 'completed' | 'overdue' | 'default';

interface TaskExecutionCockpitProps {
  executionId: string;
  processTitle: string;
  stagePath: string;
  statusLabel: string;
  statusVariant?: CockpitStatusVariant;
  progressPct: number;
  checklistDone: number;
  checklistTotal: number;
  pendenciesCount: number;
  slaLabel: string;
  slaTone?: 'default' | 'ok' | 'warning' | 'danger';
  timeLabel: string;
  timeTone?: 'default' | 'ok' | 'warning' | 'danger' | 'info';
  responsible?: string | null;
}

const VALUE_TONE: Record<NonNullable<CockpitMetric['tone']>, string> = {
  default: 'text-white',
  ok: 'text-emerald-400',
  warning: 'text-amber-400',
  danger: 'text-red-400',
  info: 'text-blue-300',
};

const SLA_TONE: Record<NonNullable<TaskExecutionCockpitProps['slaTone']>, string> = {
  default: 'text-white',
  ok: 'text-emerald-400',
  warning: 'text-amber-400',
  danger: 'text-red-400',
};

const TIME_TONE: Record<NonNullable<TaskExecutionCockpitProps['timeTone']>, string> = {
  default: 'text-white',
  ok: 'text-emerald-400',
  warning: 'text-amber-400',
  danger: 'text-red-400',
  info: 'text-blue-300',
};

const STATUS_ACCENT: Record<CockpitStatusVariant, string> = {
  paused: 'bg-amber-500',
  in_progress: 'bg-blue-500',
  completed: 'bg-emerald-500',
  overdue: 'bg-red-500',
  default: 'bg-slate-500',
};

function CockpitKpi({
  label,
  value,
  tone = 'default',
  large,
}: {
  label: string;
  value: string;
  tone?: CockpitMetric['tone'];
  large?: boolean;
}) {
  return (
    <div className="min-w-[64px]">
      <div className={`font-bold tabular-nums leading-none ${large ? 'text-[28px]' : 'text-[18px]'} ${VALUE_TONE[tone ?? 'default']}`}>
        {value}
      </div>
      <div className="mt-1 text-[7px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

export const TaskExecutionCockpit: React.FC<TaskExecutionCockpitProps> = ({
  executionId,
  processTitle,
  stagePath,
  statusLabel,
  statusVariant = 'default',
  progressPct,
  checklistDone,
  checklistTotal,
  pendenciesCount,
  slaLabel,
  slaTone = 'default',
  timeLabel,
  timeTone = 'info',
  responsible,
}) => {
  const showChecklist = checklistTotal > 0;
  const showPendencies = pendenciesCount > 0;
  const checklistValue = `${checklistDone}/${checklistTotal}`;
  const pendTone: CockpitMetric['tone'] = pendenciesCount <= 2 ? 'warning' : 'danger';
  const progressTone: CockpitMetric['tone'] =
    progressPct >= 100 ? 'ok' : progressPct > 0 ? 'info' : 'default';

  return (
    <header className="relative shrink-0 bg-slate-900 text-white dark:bg-slate-950">
      <div className={`absolute inset-y-0 left-0 w-1 ${STATUS_ACCENT[statusVariant]}`} />

      <div className="px-4 py-3 pl-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[9px] text-slate-500">
              {processTitle}
              <span className="mx-1 text-slate-600">·</span>
              <span className="font-mono">{executionId}</span>
              {responsible && (
                <>
                  <span className="mx-1 text-slate-600">·</span>
                  {responsible}
                </>
              )}
            </p>
            <h1 className="mt-0.5 text-[20px] font-bold leading-tight tracking-tight sm:text-[24px]" title={stagePath}>
              {stagePath}
            </h1>
          </div>
          <span className="rounded bg-white/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-slate-200">
            {statusLabel}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
          <CockpitKpi label="Progresso" value={`${progressPct}%`} tone={progressTone} large />
          {showChecklist && (
            <>
              <div className="hidden h-8 w-px bg-slate-700 sm:block" />
              <CockpitKpi label="Checklist" value={checklistValue} tone={checklistDone === checklistTotal ? 'ok' : 'default'} />
            </>
          )}
          {showPendencies && (
            <CockpitKpi label="Pendências" value={String(pendenciesCount)} tone={pendTone} />
          )}
          <CockpitKpi label="SLA" value={slaLabel} tone={slaTone === 'danger' ? 'danger' : slaTone === 'warning' ? 'warning' : 'default'} />
          <CockpitKpi label="Tempo" value={timeLabel} tone={timeTone} />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-800">
            <div
              className={`h-full rounded-full transition-all ${
                progressPct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.max(progressPct, progressPct > 0 ? 3 : 0)}%` }}
            />
          </div>
          <span className="shrink-0 font-mono text-[9px] tabular-nums text-slate-500">
            execução {progressPct}%
          </span>
        </div>
      </div>
    </header>
  );
};

export default TaskExecutionCockpit;
